import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { ButtonSize, PrimaryButton } from 'streamr-ui'
import { MqDesktop } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import useChainId from '~/hooks/useChainId'
import useMetamaskProvider from '~hooks/useMetamaskProvider'
import useWatchToken from '~hooks/useWatchToken'
import ChainSelector from '~shared/ChainSelector'
import ChainSelectorItem from '~shared/ChainSelector/Item'
import { MAINNET_CHAIN_ID, networks } from '~utils/ethereum'
import MetamaskAdapter from '~utils/MetamaskAdapter'

const Filler = styled.div``

const IconWrapper = styled.span`
    width: 12px;
    display: inline-flex;
    padding-right: 8px;
`

const PlusIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M1.5 5.063a.937.937 0 0 0 0 1.875h3.562v3.4a.937.937 0 1 0 1.875 0v-3.4H10.5a.937.937 0 1 0 0-1.875H6.937V1.5a.937.937 0 1 0-1.875 0v3.563H1.5Z"
            fill="#fff"
        />
    </svg>
)

const ButtonGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-direction: column;

    ${PrimaryButton} {
        align-self: center;
        width: 100%;
    }

    ${PrimaryButton}[disabled] svg {
        opacity: 0.5;
    }

    @media ${MqDesktop} {
        flex-direction: row;

        ${PrimaryButton} {
            width: auto;
        }
    }
`

const chainOptions = Object.keys(networks).map((id) => ({
    id,
    name: networks[id].name,
}))

const UnstyledSelectChain = (props) => {
    const [activeChain, setActiveChain] = useState(MAINNET_CHAIN_ID)
    const [{ adapter, connecting }, setAdapter] = useReducer(
        (state, { adapter: nextAdapter, connecting: nextConnecting }) => ({
            adapter: nextAdapter || state.adapter,
            connecting: nextConnecting,
        }),
        {
            adapter: undefined,
            connecting: false,
        },
    )
    const [canWatchToken, watchToken] = useWatchToken({
        adapter,
        chainId: activeChain,
    })
    const { chainId, switchChain, switchPending } = useChainId(adapter)
    const metamaskProvider = useMetamaskProvider()
    const allDisabled = !metamaskProvider

    const onClickAddChain = useCallback(async () => {
        if (!adapter) {
            return
        }

        if (!adapter.isConnected()) {
            await adapter.connect()
        }

        await switchChain(activeChain)
    }, [adapter, switchChain, activeChain])

    const onClickAddToken = useCallback(async () => {
        if (!adapter || !canWatchToken) {
            return
        }

        if (!adapter.isConnected()) {
            await adapter.connect()
        }

        // Before watching token we need to activate right network
        await switchChain(activeChain)
        await watchToken()
    }, [adapter, canWatchToken, switchChain, activeChain, watchToken])

    const connectWallet = useCallback(async () => {
        let metamaskAdapter
        try {
            setAdapter({
                connecting: true,
            })
            metamaskAdapter = new MetamaskAdapter()

            await metamaskAdapter.connect()
        } catch (e) {
            console.warn('Failed to connect to wallet', e)
        } finally {
            setAdapter({
                adapter: metamaskAdapter,
                connecting: false,
            })
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            if (!metamaskProvider) {
                return
            }

            // use experimental Metamask API to connect wallet without prompting if unlocked
            const { _metamask } = metamaskProvider

            if (_metamask && _metamask.isUnlocked && typeof _metamask.isUnlocked === 'function') {
                const isUnlocked = await _metamask.isUnlocked()

                if (isUnlocked) {
                    await connectWallet()
                }
            }
        })()
    }, [metamaskProvider, connectWallet])

    useEffect(() => {
        if (!adapter) {
            return undefined
        }

        return () => {
            adapter.disconnect()
        }
    }, [adapter])

    const isMainnet = useMemo(
        () => !!activeChain && activeChain === MAINNET_CHAIN_ID,
        [activeChain],
    )

    return (
        <div {...props}>
            <ChainSelector
                chains={chainOptions}
                selectedChainId={activeChain}
                onChainSelected={(nextChainId) => setActiveChain(nextChainId)}
                disabled={allDisabled || !adapter || switchPending}
            />
            <Filler />
            <ButtonGrid>
                {!adapter && (
                    <PrimaryButton
                        size={ButtonSize.Moose}
                        onClick={() => void connectWallet()}
                        disabled={allDisabled || connecting}
                    >
                        Connect Wallet
                    </PrimaryButton>
                )}
                {!!adapter && (
                    <>
                        <PrimaryButton
                            size={ButtonSize.Moose}
                            onClick={() => void onClickAddChain()}
                            disabled={
                                allDisabled || isMainnet || activeChain === chainId || switchPending
                            }
                        >
                            <IconWrapper>
                                <PlusIcon />
                            </IconWrapper>
                            Add chain
                        </PrimaryButton>
                        <PrimaryButton
                            size={ButtonSize.Moose}
                            onClick={() => void onClickAddToken()}
                            disabled={allDisabled || !canWatchToken || switchPending}
                        >
                            <IconWrapper>
                                <PlusIcon />
                            </IconWrapper>
                            Add token
                        </PrimaryButton>
                    </>
                )}
            </ButtonGrid>
        </div>
    )
}

const SelectChain = styled(UnstyledSelectChain)`
    display: flex;
    flex-direction: column;
    gap: 16px;

    ${Filler} {
        display: none;
    }

    > ${ChainSelectorItem} {
        margin: 0 -16px 0 -8px;
        height: auto;
    }

    @media ${MqDesktop} {
        flex-direction: row;

        > ${ChainSelectorItem} {
            margin: 0;
        }

        ${Filler} {
            flex: 1;
            display: block;
        }
    }
`

export default SelectChain
